'use client';

import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import { Box, IconButton, HStack, Icon, useBreakpointValue, ButtonGroup, Text, Stack, Link } from "@chakra-ui/react";
import { useState } from "react";
import { I18nLink } from "../i18n/I18nLink";
import ArrowRightSvgIcon from "@/shared/icons/ArrowRightSvgIcon";
import ArrowLeftSvgIcon from "@/shared/icons/ArrowLeftSvgIcon";
import { useTranslations } from "next-intl";

interface CardCarouselProps {
  title?: string;
  link?: string;
  items: React.ReactNode[];
  isNavigationHidden?: boolean;
}

export const CardCarousel: React.FC<CardCarouselProps> = ({ title, link, items, isNavigationHidden = true }) => {
  const t = useTranslations();
  const [loaded, setLoaded] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);

  const slidesPerView = useBreakpointValue({
    base: 1.2,   // мобильные
    sm: 2.3,     // маленькие экраны
    md: 3.3,     // средние экраны
    lg: 4.3      // большие экраны
  });

  const [sliderRef, instanceRef] = useKeenSlider<HTMLDivElement>({
    loop: false,
    renderMode: "precision",
    slides: {
      perView: slidesPerView ?? 1.2, // fallback на мобильный
      spacing: 20,  // Отступ между карточками (px)
      origin: "auto", // ← добавь это!
    },
    slideChanged(slider) {
      setCurrentSlide(slider.track.details.rel);
    },
    created() {
      setLoaded(true);
    }
  });

  const slideCount = instanceRef.current
    ? instanceRef.current.track.details.maxIdx + 1
    : items.length;

  return (
    <Stack gap={{ base: "40px", md: "40px" }}>
      <HStack justify="space-between">
        <HStack gap="16px">
          <Box>
            {title && <Text textStyle="bold16">{title}</Text>}
          </Box>
          <ButtonGroup gap="8px">
            <IconButton
              aria-label="Prev"
              onClick={() => instanceRef.current?.prev()}
              size={"2xs"}
              variant="plain"
              disabled={currentSlide === 0}
            >
              <Icon fill={currentSlide === 0 ? "#BFBFBF" : "currentcolor"} as={ArrowLeftSvgIcon} boxSize={6} />
            </IconButton>
            <IconButton
              aria-label="Next"
              onClick={() => instanceRef.current?.next()}
              variant="plain"
              size={"2xs"}
              disabled={currentSlide === (instanceRef.current?.track.details.maxIdx ?? 0)}
            >
              <Icon fill={currentSlide === (instanceRef.current?.track.details.maxIdx ?? 0) ? "#BFBFBF" : "currentcolor"} as={ArrowRightSvgIcon} boxSize={6} />
            </IconButton>
          </ButtonGroup>
        </HStack>
        {link && <Link as={I18nLink} href={link} textStyle={"bold16"} color="brand">{t('view_all')}</Link>}
      </HStack>
      <Box position="relative" w="100%">
        <div ref={sliderRef} className="keen-slider">
          {items.length
            ? items.map((card, idx) => (
              <Box className="keen-slider__slide" key={idx}>
                {card}
              </Box>
            ))
            : (
              <Box className="keen-slider__slide">
                {/* Можно отобразить заглушку или пустую карточку */}
                {t('no_products')}
              </Box>
            )
          }
        </div>
        {loaded && instanceRef.current && (
          <>
            {/* <HStack position="absolute" top="50%" left={2} right={2} justify="space-between" zIndex={2}>
              <IconButton
                aria-label="Prev"
                onClick={() => instanceRef.current?.prev()}
                variant="plain"
                disabled={currentSlide === 0}
              >
                <Icon as={MdChevronLeft} boxSize={10} />
              </IconButton>
              <IconButton
                aria-label="Next"
                onClick={() => instanceRef.current?.next()}
                variant="plain"
                disabled={currentSlide === (instanceRef.current?.track.details.maxIdx ?? 0)}
              >
                <Icon as={MdChevronRight} boxSize={10} />
              </IconButton>
            </HStack> */}
            {/* Точки навигации */}
            {!isNavigationHidden &&
              <HStack justify="center" mt={2}>
                {Array.from({ length: slideCount }).map((_, idx) => (
                  <Box
                    key={idx}
                    w={3}
                    h={3}
                    borderRadius="full"
                    bg={idx === currentSlide ? "blue.500" : "gray.300"}
                    cursor="pointer"
                    onClick={() => instanceRef.current?.moveToIdx(idx)}
                  />
                ))}
              </HStack>
            }
          </>
        )}
      </Box>
    </Stack>
  );
};